import React, { useState } from 'react';
import { css } from '@emotion/css';

import { APP_CONFIG } from '../../../config';
import { useMembership } from '../../../customHooks/useMembership';
import { useProducts } from '../../../customHooks/useProducts';
import { useGetBenefitsListPage } from '../../../customHooks/useGetBenefitsListPage.hook';
import { hasEligibleFreeExtensionEntitlement } from './freeExtensionCancellation.utils';
import { BenefitsListPage } from '@/@types/benefitsListPage';
import { BASE_PATH, CREDIT_HISTORIE } from '@/domain/routes';
import { default as Msg } from '@/i18n/components/FormattedMessage';
import { useGetEntitlementsV2 } from '@/apiHooks/entitlementsV2.hook';
import {
  useGetFreeExtensionCancellationStatus,
  useRequestFreeExtensionCancellation,
} from '@/apiHooks/freeExtensionCancellation.hook';
import { useGetHasProduct } from '@/customHooks/useGetHasProduct.hook';
import { ProductType } from '@/@types/product';
import { getCreditHistoryPathSegment } from '@/components/content/creditHistoryPage/creditHistory.util';

const styles = {
  pendingCancellation: css({
    color: 'var(--color-text-default)',
    '&, & span, & i': {
      color: 'var(--color-text-default)',
    },
  }),
};

export const MembershipManagement = () => {
  const { contractSource } = useMembership();
  const { products } = useProducts();
  const benefitsListPage = useGetBenefitsListPage();
  const { entitlements } = useGetEntitlementsV2();
  const [hasRequestedCancellation, setHasRequestedCancellation] = useState(false);
  const hasProducts = Boolean(products?.length);
  const hasKaeuferPlus = useGetHasProduct(ProductType.KaeuferPlus);
  const hasMieterPlus = useGetHasProduct(ProductType.MieterPlus);
  const hasKauferOrSuchenPlus =
    (hasKaeuferPlus && benefitsListPage === BenefitsListPage.KauferPlus) ||
    (hasMieterPlus && benefitsListPage === BenefitsListPage.MieterPlus);

  const canRequestFreeExtensionCancellation = hasEligibleFreeExtensionEntitlement(entitlements);
  const { requestedAt, initializing: cancellationStatusInitializing } = useGetFreeExtensionCancellationStatus(
    canRequestFreeExtensionCancellation,
  );
  const { isLoading: isRequestingCancellation, requestCancellation } = useRequestFreeExtensionCancellation();

  if (!contractSource && !hasProducts) {
    return null;
  }

  const defaultSource = hasProducts ? 'CHECKOUT' : 'MYSCOUT';
  const contractOverviewUrl =
    APP_CONFIG.contractOverviewUrl[contractSource ?? defaultSource] ?? APP_CONFIG.contractOverviewUrl[defaultSource];
  const shouldShowPendingCancellationMessage = Boolean(requestedAt) || hasRequestedCancellation;

  const handleCancellationRequest = async () => {
    setHasRequestedCancellation(true);

    try {
      const response = await requestCancellation();
      setHasRequestedCancellation(Boolean(response.requestedAt));
    } catch {
      setHasRequestedCancellation(false);
    }
  };

  const creditHistorieUrl = `${BASE_PATH}${CREDIT_HISTORIE}/${getCreditHistoryPathSegment(benefitsListPage)}`;

  return (
    <div>
      <h2 className='font-heading-small-regular'>
        <Msg id='membershipManagement' defaultMessage='Manage membership' />
      </h2>
      <div className='with-icon'>
        <i className='is24-icon-chevron-right' />
        <a href={contractOverviewUrl}>
          <Msg id='membershipManagement' defaultMessage='Manage membership' />
        </a>
      </div>
      {hasKauferOrSuchenPlus && (
        <div className='with-icon'>
          <i className='is24-icon-chevron-right' />
          <a href={creditHistorieUrl}>
            <Msg id='creditsTransactionsLog.link' defaultMessage='Credits Transactions Log' />
          </a>
        </div>
      )}
      {canRequestFreeExtensionCancellation && !cancellationStatusInitializing && (
        <div className={`with-icon ${shouldShowPendingCancellationMessage ? styles.pendingCancellation : ''}`}>
          <i className={shouldShowPendingCancellationMessage ? 'is24-icon-info' : 'is24-icon-chevron-right'} />
          {shouldShowPendingCancellationMessage ? (
            <span>
              <Msg
                id='membershipManagementCancellationPending'
                defaultMessage='Your cancellation is being processed and will be handled shortly.'
              />
            </span>
          ) : (
            <button
              type='button'
              className='button-link'
              disabled={isRequestingCancellation}
              onClick={handleCancellationRequest}
            >
              <Msg id='membershipManagementCancelFreeExtension' defaultMessage='Disable free renewal' />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
